import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    output,
    signal,
} from '@angular/core';
import {
    takeUntilDestroyed,
    toObservable,
    toSignal,
} from '@angular/core/rxjs-interop';
import { Router, RouterLink } from '@angular/router';
import { CurrentUser } from '@interacta-shared/data-access-auth';
import { GoogleAnalyticsService } from '@interacta-shared/data-access-common';
import {
    ConfigurationService,
    CustomerLogo,
    InstalledFeaturesService,
} from '@interacta-shared/data-access-configuration';
import { Theme, ThemeOption } from '@interacta-shared/data-access-theme';
import { NotificationPanelToggleButtonComponent } from '@interacta-shared/feature-notification';
import {
    AvatarComponent,
    BreakpointService,
    IconButtonComponent,
    ImageComponent,
    LiveStatusComponent,
} from '@interacta-shared/ui';
import {
    AIDiscoveryCoachMarkCloseReason,
    AIDiscoveryCoachMarkComponent,
    AIDiscoveryDialogComponent,
    AIHeaderButtonComponent,
} from '@interacta-shared/ui-ai';
import {
    BackComponent,
    UserMenuButtonComponent,
    UserMenuComponent,
} from '@interacta-shared/ui-common';
import { filterMap, isDefined } from '@interacta-shared/util';
import { Language } from '@interacta-shared/util-common';
import { CONVO_AI_ENABLED_BASE_ROUTES } from '@modules/ai-convo/constants';
import {
    AI_DISCOVERY_VIDEO_EN_FILE_NAME,
    AI_DISCOVERY_VIDEO_IT_FILE_NAME,
} from '@modules/ai-shared/constants';
import { AIDiscoveryService } from '@modules/ai-shared/services/ai-discovery.service';
import { AISharedActions } from '@modules/ai-shared/store/ai-shared.actions';
import { selectAIIsSidePanelOpen } from '@modules/ai-shared/store/ai-shared.selectors';
import { digitalWorkplace, IconMode } from '@modules/app-routing/routes';
import { AppSelectors, AppState } from '@modules/core/store';
import {
    selectCurrentHomeId,
    selectHomeSelectorItems,
} from '@modules/header/store/header.selectors';
import { ChatStateService } from '@modules/state/services/chat-state.service';
import { LinkDialogStateService } from '@modules/state/services/links-dialog-state.service';
import { StateService } from '@modules/state/services/state.service';
import { UIStateService } from '@modules/state/services/ui-state.service';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { filter, map, pairwise, switchMap, take } from 'rxjs/operators';
import { HomeSelectorComponent } from '../home-selector/home-selector.component';
import { SearchComponent } from '../search/search.component';

@Component({
    selector: 'interacta-header',
    templateUrl: './header.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        AIDiscoveryCoachMarkComponent,
        AIDiscoveryDialogComponent,
        AIHeaderButtonComponent,
        AvatarComponent,
        BackComponent,
        CdkOverlayOrigin,
        HomeSelectorComponent,
        IconButtonComponent,
        ImageComponent,
        LiveStatusComponent,
        NgClass,
        NgTemplateOutlet,
        NotificationPanelToggleButtonComponent,
        RouterLink,
        SearchComponent,
        TranslatePipe,
        UserMenuButtonComponent,
        UserMenuComponent,
    ],
})
export class HeaderComponent {
    private readonly stateService = inject(StateService);
    private readonly uiStateService = inject(UIStateService);
    private readonly configurationService = inject(ConfigurationService);
    private readonly installedFeaturesService = inject(
        InstalledFeaturesService,
    );
    private readonly store = inject(Store<AppState>);
    private readonly router = inject(Router);
    private readonly linkDialogStateService = inject(LinkDialogStateService);
    private readonly chatStateService = inject(ChatStateService);
    private readonly breakpointService = inject(BreakpointService);
    private readonly gaService = inject(GoogleAnalyticsService);
    private readonly aiDiscoveryService = inject(AIDiscoveryService);

    readonly user = input.required<CurrentUser>();
    readonly customerLogoSrc = input<CustomerLogo>();
    readonly iconMode = input<IconMode>('menu');
    readonly currentTheme = input.required<Theme>();

    readonly changeLanguage = output<Language>();
    readonly changeTheme = output<ThemeOption>();
    readonly logout = output<void>();
    readonly openAppInfo = output<void>();
    readonly openProfile = output<void>();
    readonly openNotificationsSettings = output<void>();

    readonly availableLanguages = this.configurationService.getAppLanguages();
    readonly isUserMenuOpen = signal(false);
    readonly currentLanguage = this.configurationService.currentLanguage;

    private readonly baseRoute = toSignal(
        this.store.select(AppSelectors.selectRouteState).pipe(
            filter(isDefined),
            filterMap((route) => route.appBaseRoute ?? undefined),
        ),
    );

    private readonly aiConvoEnabled =
        this.installedFeaturesService.getInstalledFeature('aiConvo');

    readonly showSearchBar = computed(() => {
        const baseRoute = this.baseRoute();
        return baseRoute != null
            ? !['post-print', 'community-detail'].includes(baseRoute)
            : false;
    });

    readonly showAIButton = computed(() => {
        const baseRoute = this.baseRoute();
        if (baseRoute == null) {
            return false;
        }
        return (
            CONVO_AI_ENABLED_BASE_ROUTES.includes(baseRoute) &&
            (this.aiConvoEnabled() ?? false)
        );
    });

    readonly notPostPrint = computed(() => {
        const baseRoute = this.baseRoute();
        return baseRoute != null ? baseRoute !== 'post-print' : false;
    });

    readonly homeSelectorItems = this.store.selectSignal(
        selectHomeSelectorItems,
    );
    readonly currentHomeId = this.store.selectSignal(selectCurrentHomeId);

    readonly canShowChat = toSignal(this.stateService.chatState.canShow$, {
        initialValue: false,
    });
    readonly hasUnreadChatNotifications = toSignal(
        this.stateService.chatState.unreadChatStatus$.pipe(
            map((value) => value > 0),
        ),
        { initialValue: false },
    );
    readonly newUnreadChats = toSignal(
        this.stateService.chatState.unreadChatStatus$.pipe(
            pairwise(),
            map(([previous, current]) => (current > previous ? current : null)),
        ),
        { initialValue: null },
    );

    readonly isLg = computed(
        () => this.breakpointService.breakpointsSignal()?.isLg,
    );

    readonly isAISidePanelOpen = this.store.selectSignal(
        selectAIIsSidePanelOpen,
    );

    readonly isAIDiscoveryCoachMarkOpen =
        this.aiDiscoveryService.isCoachMarkOpen;
    readonly isAIDiscoveryDialogOpen = this.aiDiscoveryService.isDialogOpen;
    readonly aiDiscoveryVideoSrc = computed(
        () =>
            this.configurationService.getSharedAssetUrl(
                this.currentLanguage().code === 'it'
                    ? AI_DISCOVERY_VIDEO_IT_FILE_NAME
                    : AI_DISCOVERY_VIDEO_EN_FILE_NAME,
            ) ?? '',
    );

    readonly areHomesAvailable = computed(
        () => this.homeSelectorItems().length > 0,
    );
    readonly showHomeSelector = computed(
        () => this.areHomesAvailable() && this.currentHomeId() != null,
    );

    constructor() {
        toObservable(this.showAIButton)
            .pipe(
                filter((showAIButton) => showAIButton),
                take(1),
                switchMap(() => this.aiDiscoveryService.nextOccasion()),
                takeUntilDestroyed(),
            )
            .subscribe(() => {
                if (!this.isAISidePanelOpen()) {
                    this.aiDiscoveryService.openCoachMark();
                }
            });
    }

    openLinksDialog(): void {
        this.linkDialogStateService.open();
    }

    toggleChat(): void {
        if (this.chatStateService.isOpen()) {
            this.chatStateService.toggleCollapse();
        } else {
            this.chatStateService.open();
            this.chatStateService.openChatList();
        }
    }

    toggleMenu(): void {
        const currState =
            this.stateService.uiState.leftMenuUI.staticData()?.isOpen;
        const nextState = !currState;
        this.uiStateService.changeLeftMenuOpen(nextState);
    }

    changeCurrentHome(homeId: number): void {
        this.router.navigate(['/', digitalWorkplace, homeId]);
    }

    setAIPanelOpen(isOpen: boolean): void {
        if (isOpen) {
            this.aiDiscoveryService.aiPanelOpened();
            const baseRoute = this.baseRoute();
            this.gaService.sendCustomEvent('isp_ai_conversational_opened', {
                source:
                    baseRoute === 'post-detail'
                        ? 'post'
                        : baseRoute === 'digital-workplace'
                          ? 'home'
                          : 'community',
            });
        }
        this.store.dispatch(AISharedActions.setIsPanelOpen({ isOpen }));
    }

    closeAIDiscoveryCoachMark(reason: AIDiscoveryCoachMarkCloseReason): void {
        this.aiDiscoveryService.closeCoachMark(reason);
    }

    openAIDiscoveryDialog(): void {
        this.aiDiscoveryService.openDialog('coach_mark');
    }

    closeAIDiscoveryDialog(): void {
        this.aiDiscoveryService.closeDialog();
    }

    openAIPanelFromDiscovery(): void {
        this.aiDiscoveryService.closeDialog();
        this.setAIPanelOpen(true);
    }

    openAppInfoAndClose(): void {
        this.isUserMenuOpen.set(false);
        this.openAppInfo.emit();
    }
}
